import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './ManageTenants.css';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { getPropertyTenancyHistory, getPropertyUnitRequests } from '../../../api/services/propertyService';
import { issueEvictionNotice, releaseTenant } from '../../../api/services/rentalService';

const ManageTenants = ({ onNotify, onBack }) => {
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [tenantsError, setTenantsError] = useState('');

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      // Use browser history to go back
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Fallback: navigate to properties if no history
        navigate('/agent/properties');
      }
    }
  }, [onBack, navigate]);
  const [tenants, setTenants] = useState([]);
  const [unitSearch, setUnitSearch] = useState('');
  const [evictionModal, setEvictionModal] = useState({
    open: false,
    tenant: null,
    moveOutDate: '',
    reason: '',
  });
  const [releaseModal, setReleaseModal] = useState({
    open: false,
    tenant: null,
    releaseDate: '',
  });
  const [submittingEvictionId, setSubmittingEvictionId] = useState('');
  const [submittingReleaseId, setSubmittingReleaseId] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    // Get property data from localStorage (set by AssignedProperties)
    const propertyData = localStorage.getItem('agent_selected_property');
    if (propertyData) {
      try {
        const parsed = JSON.parse(propertyData);
        setProperty(parsed);
      } catch (e) {
        console.error('Error parsing property data:', e);
      }
    }

    // Handle browser back button
    const handlePopState = (e) => {
      e.preventDefault();
      handleBack();
    };

    // Push current state to history
    window.history.pushState({ page: 'manage-tenants' }, '', window.location.href);

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleBack]);

  useEffect(() => {
    const loadAssignedTenants = async () => {
      if (!property?.id) {
        setTenants([]);
        setLoadingTenants(false);
        return;
      }

      setLoadingTenants(true);
      setTenantsError('');
      try {
        const [tenancyRes, assignedRequestsRes] = await Promise.all([
          getPropertyTenancyHistory(property.id),
          getPropertyUnitRequests(property.id, { status: 'ASSIGNED' }).catch(() => ({ data: [] })),
        ]);
        const rows = Array.isArray(tenancyRes?.data) ? tenancyRes.data : [];
        const assignedRequests = Array.isArray(assignedRequestsRes?.data) ? assignedRequestsRes.data : [];
        const rentByTenantKey = new Map();
        assignedRequests.forEach((req) => {
          const reqUserId = String(
            req?.requesterUserId || req?.requesterId || req?.userId || req?.tenantUserId || req?.tenantId || ''
          ).trim();
          const reqUnitId = String(req?.unitId || req?.unit?.id || '').trim();
          const key = `${reqUserId}::${reqUnitId}`;
          const monthlyRent = Number(req?.monthlyRent);
          if (reqUserId && reqUnitId && Number.isFinite(monthlyRent) && monthlyRent > 0) {
            rentByTenantKey.set(key, monthlyRent);
          }
        });
        const activeRows = rows.filter((row) => String(row?.status || '').toUpperCase() === 'ACTIVE');

        const mapped = activeRows.map((row) => ({
          ...(() => {
            const rowUserId = String(row?.userId || '').trim();
            const rowUnitId = String(row?.unitId || '').trim();
            const key = `${rowUserId}::${rowUnitId}`;
            const rentFromTenancy = Number(row?.monthlyRent);
            const rentFromAssignedRequest = rentByTenantKey.get(key);
            const rentValue = Number.isFinite(rentFromTenancy) && rentFromTenancy > 0
              ? rentFromTenancy
              : Number.isFinite(rentFromAssignedRequest) && rentFromAssignedRequest > 0
                ? rentFromAssignedRequest
                : 0;
            return { rent: rentValue };
          })(),
          id: row?.id || `${row?.userId || ''}-${row?.unitId || ''}`,
          assignmentId: row?.assignmentId || row?.id || '',
          name: [row?.userFirstName, row?.userLastName].filter(Boolean).join(' ') || row?.userEmail || 'Tenant',
          unit: row?.unitNumber || row?.unitName || '—',
          email: row?.userEmail || '—',
          moveInDate: row?.startDate || '—',
          status: String(row?.status || 'ACTIVE').toLowerCase(),
          rentDueDate: row?.rentDueDate || '',
        }));

        setTenants(mapped);
      } catch (e) {
        setTenants([]);
        setTenantsError(e?.message || 'Failed to load assigned tenants.');
      } finally {
        setLoadingTenants(false);
      }
    };

    loadAssignedTenants();
  }, [property?.id]);

  const fmtKES = (n) => `KES ${Number(n).toLocaleString()}`;

  const openEvictionModal = (tenant) => {
    setActionError('');
    setActionMessage('');
    setEvictionModal({
      open: true,
      tenant,
      moveOutDate: new Date().toISOString().substring(0, 10),
      reason: '',
    });
  };

  const closeEvictionModal = () => {
    if (submittingEvictionId) return;
    setEvictionModal({
      open: false,
      tenant: null,
      moveOutDate: '',
      reason: '',
    });
  };

  const submitEvictionNotice = async () => {
    const tenant = evictionModal.tenant;
    if (!tenant) return;
    const assignmentId = String(tenant.assignmentId || tenant.id || '').trim();
    if (!assignmentId) {
      setActionError('Missing assignment ID for this tenant.');
      return;
    }
    const moveOutDate = evictionModal.moveOutDate;
    if (!moveOutDate) {
      setActionError('Move-out date is required.');
      return;
    }
    const reason = String(evictionModal.reason || '').trim();
    if (!reason) {
      setActionError('Reason is required.');
      return;
    }

    try {
      setSubmittingEvictionId(assignmentId);
      setActionError('');
      const res = await issueEvictionNotice(assignmentId, { moveOutDate, reason });
      const msg = res?.message || `Eviction notice issued for ${tenant.name}.`;
      setActionMessage(msg);
      if (onNotify) onNotify(msg);
      closeEvictionModal();
    } catch (e) {
      const msg = e?.message || 'Failed to issue eviction notice.';
      setActionError(msg);
      if (onNotify) onNotify(msg);
    } finally {
      setSubmittingEvictionId('');
    }
  };

  const openReleaseModal = (tenant) => {
    setReleaseModal({
      open: true,
      tenant,
      releaseDate: new Date().toISOString().substring(0, 10),
    });
  };

  const closeReleaseModal = () => {
    setReleaseModal({
      open: false,
      tenant: null,
      releaseDate: '',
    });
  };

  const submitReleaseTenant = async () => {
    const { tenant, releaseDate } = releaseModal;
    if (!tenant) return;
    const assignmentId = tenant.assignmentId;
    if (!assignmentId) {
      setActionError('Missing assignment ID for this tenant.');
      return;
    }

    try {
      setSubmittingReleaseId(assignmentId);
      setActionError('');
      const res = await releaseTenant(assignmentId, releaseDate);
      const msg = res?.message || `Tenant ${tenant.name} has been released successfully.`;
      setActionMessage(msg);
      if (onNotify) onNotify(msg);

      setTenants(prev => prev.filter(t => t.id !== tenant.id));

      closeReleaseModal();
    } catch (err) {
      setActionError(err?.message || 'Could not release tenant.');
    } finally {
      setSubmittingReleaseId('');
    }
  };

  const filteredTenants = useMemo(() => {
    const q = String(unitSearch || '').trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((tenant) => String(tenant?.unit || '').toLowerCase().includes(q));
  }, [tenants, unitSearch]);

  return (
    <div className="manage-tenants-page">
      <CollapsibleTopPanel id="manage-tenants-top-panel" defaultOpenOnMobile={false}>
        <div className="page-header">
          <h2>Manage Tenants – {property?.name || 'Property'}</h2>
          <div className="manage-tenants-search">
            <label htmlFor="manage-tenants-unit-search">Search unit number</label>
            <input
              id="manage-tenants-unit-search"
              type="text"
              value={unitSearch}
              onChange={(e) => setUnitSearch(e.target.value)}
              placeholder="e.g. B12"
            />
          </div>
        </div>
      </CollapsibleTopPanel>

      <div className="tenants-container">
        {actionError ? (
          <div className="no-tenants" style={{ padding: '14px 16px', marginBottom: '12px', borderStyle: 'solid', borderColor: '#fecaca' }}>
            <p className="muted" style={{ color: '#991b1b', margin: 0 }}>{actionError}</p>
          </div>
        ) : null}
        {actionMessage ? (
          <div className="no-tenants" style={{ padding: '14px 16px', marginBottom: '12px', borderStyle: 'solid', borderColor: '#bbf7d0' }}>
            <p className="muted" style={{ color: '#166534', margin: 0 }}>{actionMessage}</p>
          </div>
        ) : null}
        {loadingTenants ? (
          <div className="no-tenants">
            <p className="muted">Loading assigned tenants...</p>
          </div>
        ) : tenantsError ? (
          <div className="no-tenants">
            <p className="muted">{tenantsError}</p>
          </div>
        ) : filteredTenants.length > 0 ? (
          <div className="tenants-grid">
            {filteredTenants.map((tenant) => (
              <div key={tenant.id} className="tenant-card">
                <div className="tenant-info-section">
                  <label className="tenant-label">Tenant</label>
                  <div className="tenant-name-unit">{tenant.name} – Unit {tenant.unit}</div>
                  <div className="tenant-details">
                    <div className="tenant-detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{tenant.email}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="detail-label">Move-in:</span>
                      <span className="detail-value">{tenant.moveInDate}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="detail-label">Monthly Rent:</span>
                      <span className="detail-value rent-amount">{fmtKES(tenant.rent)}</span>
                    </div>
                    {tenant.rentDueDate && (
                      <div className="tenant-detail-item">
                        <span className="detail-label">Rent Due Date:</span>
                        <span className="detail-value">{tenant.rentDueDate}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="tenant-actions">
                  <button
                    className="btn release"
                    onClick={() => openReleaseModal(tenant)}
                    disabled={Boolean(submittingEvictionId) || Boolean(submittingReleaseId)}
                  >
                    Release
                  </button>
                  <button
                    className="btn primary"
                    onClick={() => openEvictionModal(tenant)}
                    disabled={Boolean(submittingEvictionId) || Boolean(submittingReleaseId)}
                  >
                    Issue Eviction
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-tenants">
            <p className="muted">
              {unitSearch ? 'No tenants found for that unit search.' : 'No tenants found for this property.'}
            </p>
          </div>
        )}
      </div>

      {evictionModal.open && evictionModal.tenant && typeof document !== 'undefined'
        ? createPortal(
          <div className="modal-overlay" onClick={closeEvictionModal}>
            <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Issue Eviction Notice</h2>
                <button className="close-btn" onClick={closeEvictionModal} disabled={Boolean(submittingEvictionId)}>×</button>
              </div>
              <div className="modal-form">
                <div className="form-group">
                  <label>Tenant</label>
                  <input type="text" value={evictionModal.tenant.name || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <input type="text" value={evictionModal.tenant.unit || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Move-out Date *</label>
                  <input
                    type="date"
                    value={evictionModal.moveOutDate}
                    onChange={(e) => setEvictionModal((prev) => ({ ...prev, moveOutDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Reason *</label>
                  <textarea
                    rows="4"
                    value={evictionModal.reason}
                    onChange={(e) => setEvictionModal((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="Breach of house rules (noise complaints)"
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn secondary" onClick={closeEvictionModal} disabled={Boolean(submittingEvictionId)}>
                    Cancel
                  </button>
                  <button type="button" className="btn primary btn-danger" onClick={submitEvictionNotice} disabled={Boolean(submittingEvictionId)}>
                    {submittingEvictionId ? 'Submitting...' : 'Issue Eviction'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
        : null}

      {releaseModal.open && releaseModal.tenant && typeof document !== 'undefined'
        ? createPortal(
          <div className="modal-overlay" onClick={closeReleaseModal}>
            <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Release Tenant</h2>
                <button className="close-btn" onClick={closeReleaseModal} disabled={Boolean(submittingReleaseId)}>×</button>
              </div>
              <div className="modal-form">
                <div className="form-group">
                  <label>Tenant</label>
                  <input type="text" value={releaseModal.tenant.name || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <input type="text" value={releaseModal.tenant.unit || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Release Date (Optional)</label>
                  <input
                    type="date"
                    value={releaseModal.releaseDate}
                    onChange={(e) => setReleaseModal((prev) => ({ ...prev, releaseDate: e.target.value }))}
                  />
                  <small className="form-hint">Leave empty or set to today to release them immediately.</small>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn secondary" onClick={closeReleaseModal} disabled={Boolean(submittingReleaseId)}>
                    Cancel
                  </button>
                  <button type="button" className="btn primary btn-danger" onClick={submitReleaseTenant} disabled={Boolean(submittingReleaseId)}>
                    {submittingReleaseId ? 'Submitting...' : 'Release Tenant'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default ManageTenants;
